<template>
  <module-page v-bind="pageParams" :customBreadcrumbs="customBreadcrumbs">
    <v-layout row wrap>
      <v-flex xs12 md8 lg8>
        <v-card class="mt-2 mb-5">
          <v-card-text class="pt-3">
            <template v-if="!rescheduleBtnClicked">
              <h3 class="appointment-detail__title">
                Sua vistoria de assistência técnica foi agendada para:
              </h3>
              <AssistanceSummary
                :items="items"
                :accompanied="accompanied"
                :date="formattedDate"
                :address="address"
                :requesterName="requesterName"
                :ticketCreationNotice="ticketCreationNotice"
              />
              <template v-if="showActionButtons">
                <p class="appointment-detail__options-label">
                  Deseja reagendar horário ou cancelar assistência técnica?
                </p>
                <v-card-actions
                  v-if="showActionButtons"
                  class="appointment-detail__options"
                >
                  <v-btn
                    v-on:click="cancelAppointment"
                    light
                    class="support--background btn-md"
                    :loading="isLoading"
                    color="normal"
                    >Cancelar</v-btn
                  >
                  <span>ou</span>
                  <v-btn
                    v-on:click="rescheduleBtnClicked = true"
                    dark
                    class="support--background btn-md"
                    >Reagendar</v-btn
                  >
                </v-card-actions>
              </template>
            </template>
            <template v-if="rescheduleBtnClicked">
              <h3 class="appointment-detail__title">
                Selecione uma data e horário para o reagendamento:
              </h3>
              <Reschedule></Reschedule>
            </template>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </module-page>
</template>

<script>
import axios from 'axios';
import moment, { now } from 'moment-timezone';

import supportMixin from '@/mixins/modules/support';
import ModulePage from '@/components/layout/Page';
import ScreenLoading from '@/components/loaders/ScreenLoading';
import AssistanceSummary from '@/components/AssistanceSummary';
import Reschedule from './RescheduleTicket';

import enums from '@/utils/enums';
import bus from '@/utils/bus';
import logTrace from '@/services/logTrace';

import {
  getUnitProposersInfo,
  hasPermission,
  hasPermittedRole,
  contractIsActive,
} from '@/utils';
import { APP, EVENTS, CONFIG, UNIT } from '../../../../utils/constants';

export default {
  name: 'appointment-detail',
  mixins: [supportMixin],
  components: {
    ModulePage,
    ScreenLoading,
    AssistanceSummary,
    Reschedule,
  },
  computed: {
    customBreadcrumbs() {
      return [
        {
          text: this.$t('pages.support.ticketDetails.breadcrumbLv1'),
        },
        {
          text: this.$t('pages.support.ticketDetails.appointmentDetail', {
            ticketNumber: this.ticketNumber,
          }),
        },
      ];
    },
    isDeliveryKey() {
      return !!this.$store.getters[UNIT.GET_IS_KEY_DELIVERED];
    },
    technicalAssistanceSubject() {
      const isIndirect = this.$store.getters[UNIT.GET_IS_INDIRECT];

      const isDeliveryKey = this.isDeliveryKey;
      const hasCertificateDate =
        !!this.$store.getters[UNIT.GET_CONCLUSION_CERTIFICATE_DATE];

      const canShowTechnicalAssistance =
        (isDeliveryKey || this.isSyndic) && hasCertificateDate;

      return this.$store.getters[
        CONFIG.GET
      ].navigationRules.ticketSubjects.filter((subject) => {
        const permittedRoleRule = subject.roles
          ? hasPermittedRole(subject.roles)
          : true;
        const adminProfilesRule = subject.adminProfiles
          ? hasPermission(subject.adminProfiles, false)
          : true;
        const showToIndirectRule =
          subject.showToIndirect != null && isIndirect === true
            ? subject.showToIndirect
            : true;
        const isTechnicalAssistanceEnabled =
          subject.value === 'technicalAssistanceErp'
            ? canShowTechnicalAssistance
            : true;

        return (
          permittedRoleRule &&
          adminProfilesRule &&
          showToIndirectRule &&
          isTechnicalAssistanceEnabled
        );
      });
    },
  },
  data() {
    return {
      pageParams: this.$store.getters[APP.PAGES].support,
      reasons: undefined,
      workOrdersDescription: undefined,
      items: undefined,
      accompanied: undefined,
      formattedDate: undefined,
      idTicket: '',
      isLoading: false,
      address: undefined,
      requesterName: '',
      showActionButtons: false,
      ticketNumber: null,
      rescheduleBtnClicked: false,
      ticketCreationNotice: undefined,
    };
  },
  created() {
    this.getWorkOrderDetails();
    this.mountAddress();
    bus.$on('unit:change', this.invalidTicket);
    bus.$on('unit:update', this.invalidTicket);
  },
  beforeDestroy() {
    bus.$off('unit:change', this.invalidTicket);
    bus.$off('unit:update', this.invalidTicket);
  },
  methods: {
    invalidTicket() {
      this.$router.replace({ name: 'home' });
    },
    async getWorkOrderDetails() {
      const ticketId = decodeURIComponent(
        this.$router.currentRoute.params.ticketId
      );

      let scheduledEvents =
        this.$store.getters[EVENTS.GET_TECHNICAL_ASSISTANCE] || [];
      scheduledEvents = scheduledEvents.filter(
        (schedule) => schedule.sku == this.selectedUnit.sku
      );

      const selectedTicket = scheduledEvents.find(
        (item) => item.ticketId == ticketId
      );
      if (!selectedTicket) {
        this.invalidTicket();
        return;
      }

      const proposers = getUnitProposersInfo(this.selectedUnit);

      this.reasons = selectedTicket.reasons;
      this.workOrdersDescription = selectedTicket.reasonsByDescription;
      this.items = [...this.reasons, ...this.workOrdersDescription];

      this.accompanied = selectedTicket.accompanied;
      this.formattedDate = this.formatLongDateTime(selectedTicket.date);

      const ticketRequesterDocument = selectedTicket.requesterDocument;
      const ticketrequesterName = selectedTicket.requesterName;
      let requesterIndex = 0;
      if (ticketRequesterDocument != null) {
        requesterIndex = proposers.findIndex(
          (proposer) => proposer.document == ticketRequesterDocument
        );
      } else {
        requesterIndex = proposers.findIndex(
          (proposer) =>
            proposer.name
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '') ==
            ticketrequesterName
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
        );
      }

      this.requesterName = proposers[requesterIndex].name;
      this.showActionButtons =
        requesterIndex == 0 && this.canShowActionButtons(selectedTicket.date);
      this.idTicket = ticketId;
      this.ticketNumber = selectedTicket.ticketNumber;
    },
    async cancelAppointment() {
      this.isLoading = true;
      const kitfixUrl = this.$store.getters[CONFIG.GET_KITFIX_URL];
      let url = `${kitfixUrl}/api/workOrder/cancelWorkOrders?idTicket=${this.idTicket}`;
      await this.createTicket();

      axios
        .put(url)
        .then((response) => {
          bus.$emit('snackbar:open', {
            text: 'Agendamento cancelado com sucesso',
            color: enums.snackbarColors.success,
          });
          logTrace({
            action: this.enums.logTraceActions.technicalAssistanceCancellation,
            details: {
              ticketId: this.idTicket,
            },
          });
          this.$router.push({ name: 'home' });
          this.isLoading = false;
        })
        .catch((err) => {
          this.isLoading = false;
          bus.$emit('snackbar:open', {
            text: this.$t('responses.error'),
            color: enums.snackbarColors.error,
          });
        });
    },
    async createTicket() {
      try {
        const payload = {
          summary: `CANCELAMENTO TICKET: ${this.ticketNumber}`,
          comments: {
            public: {},
          },
        };
        await this.sendTicket(payload);
      } catch (error) {
        bus.$emit('snackbar:open', {
          text: this.$t('responses.error'),
          color: enums.snackbarColors.error,
        });
      }
    },
    getAccompanied(selectedTicket) {
      if (selectedTicket.idSchedule) {
        this.accompanied = {
          name: selectedTicket.technicalName,
          document: selectedTicket.companionDocument,
        };
      }
    },
    mountAddress() {
      this.address = this.$store.getters[UNIT.GET_SELECTED_PRODUCT].address;
      this.address.product = this.selectedUnit.product.title;
      this.address.block = this.selectedUnit.block.title;
    },
    mountTicketCreationNotice() {
      const config = this.$store.getters[CONFIG.GET];

      if (config && config.texts && config.texts.ticketCreationNotice) {
        this.ticketCreationNotice = `\n${config.texts.ticketCreationNotice}\n`;
      }
    },
    canShowActionButtons(date) {
      if (this.technicalAssistanceSubject.length > 0) {
        let scheduleDate = new Date(date);
        let dateNow = new Date();
        return (
          !this.isScheduleToday(dateNow, scheduleDate) &&
          ((this.isScheduleNextDay(dateNow, scheduleDate) &&
            !this.is15hours(dateNow)) ||
            !this.isScheduleNextDay(dateNow, scheduleDate))
        );
      } else {
        return false;
      }
    },
    isScheduleToday(now, schedule) {
      return now.toLocaleDateString() === schedule.toLocaleDateString();
    },
    is15hours(now) {
      return now.getHours() >= 15;
    },
    isScheduleNextDay(now, schedule) {
      let nextDay = new Date(now.setDate(new Date().getDate() + 1));
      return schedule.toLocaleDateString() === nextDay.toLocaleDateString();
    },
  },
};
</script>

<style lang="scss" scoped>
.appointment-detail {
  &__title {
    font-size: 14px;
    display: block;
    margin: 5px 0 20px;
    font-weight: normal;
  }

  &__options-label {
    display: block;
    text-align: center;
    margin: 15px 0 5px;
  }

  &__options {
    display: flex;
    justify-content: center;

    span {
      margin: 0 10px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/support/ticket/technicalAssistance/AppointmentDetail.vue